import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Paper,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import adminAPI from "../../Services/Admin/adminAPI";
import SettingsIcon from "@mui/icons-material/Settings";
import PostMarketingCard from "../Small/PostMarketingCard";
import EditIcon from "@mui/icons-material/Edit";

const Marketing = () => {
  const [loading, setLoading] = useState(false);
  const marketingList = useRef([]);
  const [filteredMarketingList, setFilteredMarketingList] = useState([]);
  const [status, setStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showColumns, setShowColumns] = useState(() => {
    const savedColumns = localStorage.getItem("showColumnsMarketing");
    return savedColumns
      ? JSON.parse(savedColumns)
      : {
          campaign_name: true,
          status: true,
          start_date: true,
          end_date: true,
          views: true,
          clicks: true,
          duration: true, // Colonne pour durée
          redirect_url: true, // Colonne pour URL de redirection
        };
  });
  const [showDialog, setShowDialog] = useState(false);
  const [showPostMarketingCard, setShowPostMarketingCard] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  const fetchMarketing = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.fetchMarketing();
      marketingList.current = data;
      updateFilteredMarketingList();
    } catch (error) {
      console.error("Erreur lors de la récupération des données :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketing();
  }, []);

  useEffect(() => {
    localStorage.setItem("showColumnsMarketing", JSON.stringify(showColumns));
  }, [showColumns]);

  useEffect(() => {
    updateFilteredMarketingList();
  }, [searchTerm, status]);

  const updateFilteredMarketingList = () => {
    const filteredList = marketingList.current.filter((item) => {
      const matchesSearchTerm = item.campaign_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus = status === "" || item.status === status;
      return matchesSearchTerm && matchesStatus;
    });
    setFilteredMarketingList(filteredList);
  };

  const handleStatusChange = (event) => setStatus(event.target.value);
  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const handleShowColumnsDialog = () => setShowDialog(true);
  const handleCloseDialog = () => setShowDialog(false);

  const handleColumnChange = (column) => {
    setShowColumns({
      ...showColumns,
      [column]: !showColumns[column],
    });
  };

  const handleOpenPostMarketingCard = (id) => {
    setCurrentId(id);
    setShowPostMarketingCard(true);
  };

  const handleClosePostMarketingCard = () => {
    setShowPostMarketingCard(false);
    setCurrentId(null);
  };

  const isDateInRange = (startDate, endDate) => {
    const currentDate = new Date();
    return (
      new Date(startDate) <= currentDate && currentDate <= new Date(endDate)
    );
  };

  const handleSwitchChange = async (id, newStatus) => {
    const previousStatus = filteredMarketingList.find(
      (item) => item.id === id
    ).status;
    setFilteredMarketingList((prevList) =>
      prevList.map((item) =>
        item.id === id
          ? { ...item, status: newStatus ? "active" : "inactive" }
          : item
      )
    );

    try {
      await adminAPI.updateMarketingStatus(
        id,
        newStatus ? "active" : "inactive"
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut :", error);
      setFilteredMarketingList((prevList) =>
        prevList.map((item) =>
          item.id === id ? { ...item, status: previousStatus } : item
        )
      );
    }
  };

  return (
    <Card sx={{ maxWidth: 800, margin: "auto", padding: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filtres
        </Typography>
        <Box display="flex" gap={2} mb={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={handleStatusChange}>
              <MenuItem value="">Aucun</MenuItem>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par nom de campagne"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 3 }}
        />
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleOpenPostMarketingCard(null)}
            >
              + Ajouter
            </Button>
            <IconButton
              variant="outlined"
              color="secondary"
              onClick={handleShowColumnsDialog}
            >
              <SettingsIcon />
            </IconButton>
          </Stack>
        </Box>

        <Dialog open={showDialog} onClose={handleCloseDialog}>
          <DialogTitle>Afficher les colonnes</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.keys(showColumns).map((column) => (
                <FormControlLabel
                  key={column}
                  control={
                    <Checkbox
                      checked={showColumns[column]}
                      onChange={() => handleColumnChange(column)}
                    />
                  }
                  label={
                    column.charAt(0).toUpperCase() +
                    column.slice(1).replace("_", " ")
                  }
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  {showColumns.campaign_name && (
                    <TableCell>Nom de la campagne</TableCell>
                  )}
                  {showColumns.status && <TableCell>Status</TableCell>}
                  {showColumns.start_date && (
                    <TableCell>Date de début</TableCell>
                  )}
                  {showColumns.end_date && <TableCell>Date de fin</TableCell>}
                  {showColumns.views && <TableCell>Vues</TableCell>}
                  {showColumns.clicks && <TableCell>Clics</TableCell>}
                  {showColumns.duration && <TableCell>Durée (s)</TableCell>}
                  {showColumns.redirect_url && (
                    <TableCell>URL de redirection</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMarketingList.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpenPostMarketingCard(item.id)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                    {showColumns.campaign_name && (
                      <TableCell>{item.campaign_name}</TableCell>
                    )}
                    {showColumns.status && (
                      <TableCell>
                        <Switch
                          checked={item.status === "active"}
                          onChange={() =>
                            handleSwitchChange(
                              item.id,
                              item.status !== "active"
                            )
                          }
                          disabled={
                            !isDateInRange(item.start_date, item.end_date)
                          }
                        />
                      </TableCell>
                    )}
                    {showColumns.start_date && (
                      <TableCell>
                        {new Date(item.start_date).toLocaleDateString("fr-FR")}
                      </TableCell>
                    )}
                    {showColumns.end_date && (
                      <TableCell>
                        {new Date(item.end_date).toLocaleDateString("fr-FR")}
                      </TableCell>
                    )}
                    {showColumns.views && <TableCell>{item.views}</TableCell>}
                    {showColumns.clicks && <TableCell>{item.clicks}</TableCell>}
                    {showColumns.duration && (
                      <TableCell>{item.duration ?? "Non défini"}</TableCell>
                    )}
                    {showColumns.redirect_url && (
                      <TableCell>{item.redirect_url ?? "Non défini"}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </CardContent>
      <PostMarketingCard
        open={showPostMarketingCard}
        handleClose={handleClosePostMarketingCard}
        id={currentId}
      />
    </Card>
  );
};

export default Marketing;
